import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';
import { type CountryCode } from 'libphonenumber-js';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  TenantDetailPayload,
  TenantOnboardingApiService,
} from 'src/app/core/services/tenant-onboarding-api.service';
import { UiAlertService } from 'src/app/core/services/ui-alert.service';
import { IntlPhoneFieldComponent } from 'src/app/shared/components/intl-phone-field/intl-phone-field.component';
import {
  dialStringForCountry,
  normalizeNationalDigits,
  splitPhoneForForm,
  validateNationalPhone,
} from 'src/app/shared/utils/international-phone';
import {
  normalizeGstin,
  normalizePan,
  validateIndianGstin,
  validateIndianPan,
} from 'src/app/shared/utils/india-tax-ids';
import { slugifyTenantSubdomain } from 'src/app/shared/utils/string.util';
import {
  applyCommercialMetadataToSelections,
  buildCommercialMetadataJson,
  emptySelectionMap,
  hasAnyProductSelected,
  parseCatalogFromDropdownEnvelope,
  type CatalogRow,
} from 'src/app/shared/utils/tenant-onboarding-catalog';
import {
  applyTenantFormApiErrorToForm,
  parseTenantClientHttpError,
} from 'src/app/shared/utils/tenant-form-http-error';
import { formatMoneyFieldForForm } from 'src/app/shared/utils/money-field-form';

@Component({
  selector: 'app-edit-client',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    SharedModule,
    MaterialModule,
    IconModule,
    IntlPhoneFieldComponent,
  ],
  templateUrl: './edit-client.component.html',
  styleUrl: './edit-client.component.css',
})
export class EditClientComponent implements OnInit, OnDestroy {
  private static readonly UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly tenantApi = inject(TenantOnboardingApiService);
  private readonly uiAlert = inject(UiAlertService);

  @ViewChild('contactPhoneField') contactPhoneField?: IntlPhoneFieldComponent;
  @ViewChild('personPhoneField') personPhoneField?: IntlPhoneFieldComponent;

  /** Route param — public tenant identifier for URLs. */
  tenantUuid = '';
  tenantId = 0;

  /** Presigned URLs for preview (private S3). */
  logoPreviewUrl = '';
  panPreviewUrl = '';

  unlinkLogoBusy = false;
  unlinkPanBusy = false;

  showSuccessModal = false;
  private successRedirectTimer: ReturnType<typeof setTimeout> | null = null;

  bootstrapLoading = true;
  loadError = '';
  catalogLoadError = '';
  uniqueIdLabel = '';

  /** From `GET /product/types/dropdown` — labels & slugs match database. */
  productsCatalog: CatalogRow[] = [];
  productSelection: Record<string, boolean> = {};
  /**
   * Loan services are no longer edited in the UI (aligned with create-client). Existing junction
   * slugs are carried forward in `commercial_metadata` on save so unrelated edits do not clear them.
   */
  private preservedLoanServiceSlugs: string[] = [];

  /** Form fields — mirror create-client */
  businessName = '';
  tenantSubdomain = '';
  contactCountryIso: CountryCode = 'IN';
  contactNational = '';
  contactPerson = '';
  contactPersonCountryIso: CountryCode = 'IN';
  contactPersonNational = '';
  email = '';
  gstn = '';
  billingAddress = '';
  billingCity = '';
  billingPincode = '';
  businessPan = '';
  tenantStatus = 'Active';

  logoFile: File | null = null;
  logoFileName = '';
  existingLogoHint = '';
  profilePictureFile: File | null = null;
  profilePictureFileName = '';
  existingProfilePictureHint = '';
  /** True when tenant already has a stored PAN card image (`profile_picture` upload). */
  existingPanImageOnFile = false;

  amountReceived = '';
  dateOfReceipt: Date | string | null = '';
  /** Calendar date the client was loaded with; a past date is only rejected if changed from this. */
  private originalDateOfReceipt: Date | null = null;
  unNumber = '';
  /** Per-PD charge amount per product slug (string from the input). */
  productCharges: Record<string, string> = {};

  /** PREPAID tenants buy subscription plans; POSTPAID tenants are billed per PD. */
  billingType: 'PREPAID' | 'POSTPAID' = 'POSTPAID';
  billingFrequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | '' = '';
  readonly billingFrequencies = [
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
  ];

  get isPostpaid(): boolean {
    return this.billingType === 'POSTPAID';
  }

  /** Switching to prepaid clears postpaid-only inputs so stale rates are never submitted. */
  onBillingTypeChange(type: 'PREPAID' | 'POSTPAID'): void {
    this.billingType = type;
    if (type === 'PREPAID') {
      this.billingFrequency = '';
      this.productCharges = {};
    }
    this.submitError = '';
  }

  submitting = false;
  submitError = '';

  /** Inline validation messages under fields (all shown together on submit when invalid). */
  fieldErrors: Partial<
    Record<
      | 'businessName'
      | 'tenantSubdomain'
      | 'email'
      | 'contactPhone'
      | 'contactPersonPhone'
      | 'gstn'
      | 'businessPan'
      | 'panImage'
      | 'products'
      | 'dateOfReceipt',
      string
    >
  > = {};

  readonly timezoneDefault = 'Asia/Kolkata';
  timezone = 'Asia/Kolkata';
  currencyCode = 'INR';
  currencySymbol = '₹';

  ngOnInit(): void {
    const uuid = this.route.snapshot.paramMap.get('uuid')?.trim() ?? '';
    if (!uuid || !EditClientComponent.UUID_REGEX.test(uuid)) {
      this.bootstrapLoading = false;
      this.loadError = 'Invalid client link (use the tenant UUID from Manage clients).';
      return;
    }
    this.tenantUuid = uuid;
    this.loadTenantAndCatalog();
  }

  trackBySlug(_index: number, row: CatalogRow): string {
    return row.slug;
  }

  private loadTenantAndCatalog(): void {
    this.bootstrapLoading = true;
    this.loadError = '';
    this.catalogLoadError = '';
    forkJoin({
      tenant: this.tenantApi.getTenantByUuid(this.tenantUuid),
      productTypes: this.tenantApi.listProductTypesDropdown(),
    })
      .pipe(finalize(() => (this.bootstrapLoading = false)))
      .subscribe({
        next: ({ tenant, productTypes }) => {
          if (!tenant?.status || !tenant.data) {
            this.loadError = tenant?.message || 'Could not load tenant.';
            return;
          }
          const productRows = parseCatalogFromDropdownEnvelope(productTypes);
          if (productRows === null) {
            this.catalogLoadError =
              productTypes?.message || 'Could not load product types.';
            this.productsCatalog = [];
            this.productSelection = {};
            this.patchForm(tenant.data);
            return;
          }
          this.productsCatalog = productRows;
          this.productSelection = emptySelectionMap(this.productsCatalog);
          this.patchForm(tenant.data);
        },
        error: (err: unknown) => {
          this.loadError = parseTenantClientHttpError(err, 'Client not found.');
        },
      });
  }

  private patchForm(t: TenantDetailPayload): void {
    this.tenantId = t.id;
    this.uniqueIdLabel = t.unique_id ? `Reference ID: ${t.unique_id}` : '';
    this.businessName = t.tenant_name ?? '';
    this.tenantSubdomain = (t.tenant_subdomain ?? '').trim();
    this.email = (t.email ?? '').trim();
    this.tenantStatus = (t.status ?? 'Active').trim();

    const mainPhone = splitPhoneForForm(
      t.contact_number_country_code,
      t.contact_number,
      'IN',
      t.contact_number_country_iso,
    );
    this.contactCountryIso = mainPhone.iso;
    this.contactNational = mainPhone.national;

    this.contactPerson = t.contact_person ?? '';

    const personPhone = splitPhoneForForm(
      t.contact_person_phone_country_code,
      t.contact_person_phone,
      'IN',
      t.contact_person_phone_country_iso,
    );
    this.contactPersonCountryIso = personPhone.iso;
    this.contactPersonNational = personPhone.national;
    this.gstn = t.gstn ?? '';
    this.businessPan = t.business_pan ?? '';
    this.billingAddress = t.billing_address ?? '';
    this.billingCity = t.billing_city ?? '';
    this.billingPincode = t.billing_pincode ?? '';

    if (t.currency) {
      this.currencyCode = t.currency;
    }
    if (t.currency_symbol) {
      this.currencySymbol = t.currency_symbol;
    }
    if (t.timezone?.trim()) {
      this.timezone = t.timezone.trim();
    }

    this.applyTenantBranding(t);
    this.applyCommercialReceiptFields(t.commercial_profile);
    this.applyTenantCatalogSelectionsFromDetail(t);
  }

  private setSelectionFlags(slugs: string[], selection: Record<string, boolean>): void {
    for (const slug of slugs) {
      if (Object.hasOwn(selection, slug)) {
        selection[slug] = true;
      }
    }
  }

  private applyProductCharges(charges: Record<string, number>): void {
    for (const [slug, amount] of Object.entries(charges)) {
      if (amount != null) {
        this.productCharges[slug] = String(amount);
      }
    }
  }

  /** Maps junction slugs or legacy metadata into `productSelection`; preserves loan services for metadata only. */
  private applyTenantCatalogSelectionsFromDetail(t: TenantDetailPayload): void {
    // Restore the saved billing configuration; default to postpaid for tenants created before billing types existed.
    this.billingType = t.billing_type ?? 'POSTPAID';
    this.billingFrequency = t.billing_frequency ?? '';
    this.capturePreservedLoanServices(t);
    if (!this.productsCatalog.length) {
      return;
    }
    const pSel = t.selected_product_slugs ?? [];
    if (t.selected_product_slugs !== undefined) {
      this.setSelectionFlags(pSel, this.productSelection);
      const charges = (t as { product_charges?: Record<string, number> }).product_charges ?? {};
      this.applyProductCharges(charges);
      return;
    }
    const legacyServices: Record<string, boolean> = {};
    applyCommercialMetadataToSelections(
      t.commercial_profile?.metadata,
      this.productSelection,
      legacyServices,
    );
    const fromMeta = Object.entries(legacyServices)
      .filter(([, selected]) => selected)
      .map(([slug]) => slug);
    if (fromMeta.length) {
      this.preservedLoanServiceSlugs = fromMeta;
    }
  }

  private capturePreservedLoanServices(t: TenantDetailPayload): void {
    const junction = t.selected_service_slugs ?? [];
    if (junction.length) {
      this.preservedLoanServiceSlugs = [...junction];
      return;
    }
    const meta = t.commercial_profile?.metadata as { loanServices?: Record<string, boolean> } | undefined;
    const loanServices = meta?.loanServices;
    if (!loanServices || typeof loanServices !== 'object') {
      this.preservedLoanServiceSlugs = [];
      return;
    }
    this.preservedLoanServiceSlugs = Object.entries(loanServices)
      .filter(([, selected]) => selected)
      .map(([slug]) => slug);
  }

  /** Rebuilds loan-service flags for `commercial_metadata` without a services dropdown (create-client parity). */
  private loanServicesForMetadata(): {
    catalog: CatalogRow[];
    selection: Record<string, boolean>;
  } {
    const catalog = this.preservedLoanServiceSlugs.map((slug) => ({
      id: 0,
      name: slug,
      slug,
    }));
    const selection = emptySelectionMap(catalog);
    for (const slug of this.preservedLoanServiceSlugs) {
      selection[slug] = true;
    }
    return { catalog, selection };
  }

  suggestSubdomain(): void {
    const slug = slugifyTenantSubdomain(this.businessName || this.email.split('@')[0] || 'tenant');
    this.tenantSubdomain = slug || 'tenant-' + Math.random().toString(36).slice(2, 7);
  }

  maybeSuggestSubdomainFromName(): void {
    if (this.businessName.trim() && !this.tenantSubdomain.trim()) {
      this.suggestSubdomain();
    }
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.logoFile = file ?? null;
    this.logoFileName = file ? file.name : '';
  }

  onProfilePictureSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.profilePictureFile = file ?? null;
    this.profilePictureFileName = file ? file.name : '';
  }

  unlinkLogo(): void {
    if (!this.tenantId || this.unlinkLogoBusy) {
      return;
    }
    this.unlinkLogoBusy = true;
    this.submitError = '';
    this.tenantApi
      .removeTenantLogo(this.tenantId)
      .pipe(finalize(() => (this.unlinkLogoBusy = false)))
      .subscribe({
        next: (res) => {
          if (res?.status === true || res?.statusCode === 200) {
            this.logoFile = null;
            this.logoFileName = '';
            this.loadTenantAndCatalog();
            return;
          }
          this.submitError = res?.message || 'Could not remove logo.';
        },
        error: (err: unknown) => {
          this.submitError = parseTenantClientHttpError(err, 'Client not found.');
        },
      });
  }

  unlinkPanImage(): void {
    if (!this.tenantId || this.unlinkPanBusy) {
      return;
    }
    this.unlinkPanBusy = true;
    this.submitError = '';
    this.tenantApi
      .removeTenantProfilePicture(this.tenantId)
      .pipe(finalize(() => (this.unlinkPanBusy = false)))
      .subscribe({
        next: (res) => {
          if (res?.status === true || res?.statusCode === 200) {
            this.profilePictureFile = null;
            this.profilePictureFileName = '';
            this.loadTenantAndCatalog();
            return;
          }
          this.submitError = res?.message || 'Could not remove PAN image.';
        },
        error: (err: unknown) => {
          this.submitError = parseTenantClientHttpError(err, 'Client not found.');
        },
      });
  }

  blurNormalizeGst(): void {
    const n = normalizeGstin(this.gstn);
    if (n !== this.gstn) {
      this.gstn = n;
    }
  }

  blurNormalizePan(): void {
    const n = normalizePan(this.businessPan);
    if (n !== this.businessPan) {
      this.businessPan = n;
    }
  }

  clearSubmitErrorOnly(): void {
    this.submitError = '';
  }

  onDateOfReceiptChange(): void {
    this.submitError = '';
    if (!this.fieldErrors.dateOfReceipt) {
      return;
    }
    const next = { ...this.fieldErrors };
    delete next.dateOfReceipt;
    this.fieldErrors = next;
  }

  private coercedReceiptDate(): Date | null {
    if (typeof this.dateOfReceipt === 'string') {
      return new Date(this.dateOfReceipt);
    }
    if (this.dateOfReceipt instanceof Date) {
      return this.dateOfReceipt;
    }
    return null;
  }

  private parseDateOfReceiptValue(): Date | null {
    if (!this.dateOfReceipt) {
      return null;
    }
    const d = this.coercedReceiptDate();
    if (!d || Number.isNaN(d.getTime())) {
      return null;
    }
    return d;
  }

  private isCalendarDateBeforeToday(d: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const picked = new Date(d);
    picked.setHours(0, 0, 0, 0);
    return picked.getTime() < today.getTime();
  }

  private isSameCalendarDate(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private collectFieldValidationErrors(): typeof this.fieldErrors {
    const e: typeof this.fieldErrors = {};

    if (!this.businessName.trim()) {
      e.businessName = 'Business name is required.';
    }
    this.applySubdomainValidationErrors(e, this.tenantSubdomain.trim().toLowerCase());
    this.applyEmailValidationErrors(e, this.email.trim().toLowerCase());

    if (!this.hasAnyProductSelected()) {
      e.products = 'Select at least one product type.';
    }

    const contactPhoneErr = validateNationalPhone(this.contactCountryIso, this.contactNational);
    if (contactPhoneErr) {
      e.contactPhone = contactPhoneErr;
    }
    const personPhoneErr = validateNationalPhone(this.contactPersonCountryIso, this.contactPersonNational);
    if (personPhoneErr) {
      e.contactPersonPhone = personPhoneErr;
    }

    if (!normalizeGstin(this.gstn).trim()) {
      e.gstn = 'GST number (GSTIN) is required.';
    }
    if (!normalizePan(this.businessPan).trim()) {
      e.businessPan = 'Business PAN is required.';
    }
    if (!this.profilePictureFile && !this.existingPanImageOnFile) {
      e.panImage = 'PAN card image is required.';
    }

    const gstErr = validateIndianGstin(this.gstn);
    if (gstErr) {
      e.gstn = gstErr;
    }
    const panErr = validateIndianPan(this.businessPan);
    if (panErr) {
      e.businessPan = panErr;
    }

    const receipt = this.parseDateOfReceiptValue();
    const unchanged =
      receipt &&
      this.originalDateOfReceipt &&
      this.isSameCalendarDate(receipt, this.originalDateOfReceipt);
    if (receipt && !unchanged && this.isCalendarDateBeforeToday(receipt)) {
      e.dateOfReceipt = 'Date of receipt cannot be in the past.';
    }
    return e;
  }

  private validateBillingFrequency(): boolean {
    if (this.isPostpaid && !this.billingFrequency) {
      this.submitError = 'Please select a billing frequency.';
      return false;
    }
    return true;
  }

  private applySubdomainValidationErrors(
    e: typeof this.fieldErrors,
    sub: string,
  ): void {
    if (!sub) {
      e.tenantSubdomain = 'Client subdomain is required.';
      return;
    }
    if (sub.length < 3 || sub.length > 63) {
      e.tenantSubdomain = 'Subdomain must be between 3 and 63 characters.';
      return;
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(sub)) {
      e.tenantSubdomain =
        'Subdomain may only contain lowercase letters, numbers, and hyphens (cannot start/end with a hyphen).';
    }
  }

  private applyEmailValidationErrors(e: typeof this.fieldErrors, mail: string): void {
    if (!mail) {
      e.email = 'Email is required.';
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      e.email = 'Enter a valid email address.';
    }
  }

  onSubmit(): void {
    this.submitError = '';
    this.fieldErrors = {};
    this.contactPhoneField?.clearError();
    this.personPhoneField?.clearError();

    const e = this.collectFieldValidationErrors();
    this.fieldErrors = e;
    if (Object.keys(e).length > 0) {
      return;
    }
    if (!this.validateBillingFrequency()) {
      return;
    }

    const name = this.businessName.trim();
    const sub = this.tenantSubdomain.trim().toLowerCase();
    const mail = this.email.trim().toLowerCase();

    // Loan services are no longer chosen on this form; their existing selection is carried
    // through untouched, so only the product catalog gates saving.
    if (this.catalogLoadError || this.productsCatalog.length === 0) {
      this.submitError =
        this.catalogLoadError ||
        'Product type catalog did not load. Refresh the page.';
      return;
    }

    const dateIso = this.receiptDateToIso(this.dateOfReceipt);

    const { catalog: servicesCatalog, selection: serviceSelection } =
      this.loanServicesForMetadata();
    const metadata = buildCommercialMetadataJson(
      this.productsCatalog,
      servicesCatalog,
      this.productSelection,
      serviceSelection,
      this.productCharges,
    );

    const fd = new FormData();
    fd.append('tenant_name', name);
    fd.append('tenant_subdomain', sub);
    fd.append('email', mail);
    fd.append('currency', JSON.stringify({ code: this.currencyCode, symbol: this.currencySymbol }));
    fd.append('timezone', this.timezone.trim() || this.timezoneDefault);
    fd.append('status', this.tenantStatus);

    const mainDigits = normalizeNationalDigits(this.contactNational);
    fd.append('contact_number_country_code', mainDigits ? dialStringForCountry(this.contactCountryIso) : '');
    fd.append('contact_number', mainDigits);
    if (mainDigits) {
      fd.append('contact_number_country_iso', String(this.contactCountryIso).toUpperCase());
    }

    if (this.contactPerson.trim()) {
      fd.append('contact_person', this.contactPerson.trim());
    }

    const personDigits = normalizeNationalDigits(this.contactPersonNational);
    fd.append(
      'contact_person_phone_country_code',
      personDigits ? dialStringForCountry(this.contactPersonCountryIso) : '',
    );
    fd.append('contact_person_phone', personDigits);
    if (personDigits) {
      fd.append('contact_person_phone_country_iso', String(this.contactPersonCountryIso).toUpperCase());
    }
    fd.append('gstn', normalizeGstin(this.gstn));
    fd.append('business_pan', normalizePan(this.businessPan));
    // Always sent (even blank) so clearing a field clears it on the server.
    fd.append('billing_address', this.billingAddress.trim());
    fd.append('billing_city', this.billingCity.trim());
    fd.append('billing_pincode', this.billingPincode.trim());

    if (this.amountReceived.trim()) {
      fd.append('commercial_amount_received', this.amountReceived.trim());
    }
    if (dateIso) {
      fd.append('commercial_date_of_receipt', dateIso);
    }
    if (this.unNumber.trim()) {
      fd.append('commercial_un_number', this.unNumber.trim());
    }
    // Per-product per-PD charges travel inside commercial_metadata.productCharges.
    fd.append('commercial_metadata', metadata);
    fd.append('billing_type', this.billingType);
    // Omit rather than send '' for prepaid: backend's @IsOptional() only skips
    // validation for null/undefined, so '' would fail @IsIn(['DAILY','WEEKLY','MONTHLY']).
    if (this.isPostpaid) {
      fd.append('billing_frequency', this.billingFrequency);
    }

    if (this.logoFile) {
      fd.append('logo', this.logoFile, this.logoFile.name);
    }
    if (this.profilePictureFile) {
      fd.append('profile_picture', this.profilePictureFile, this.profilePictureFile.name);
    }

    this.submitting = true;
    this.tenantApi
      .updateTenant(this.tenantId, fd)
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: () => this.openSuccessModal(),
        error: (err: unknown) =>
          applyTenantFormApiErrorToForm(err, this, this.uiAlert, { notFoundMessage: 'Client not found.' }),
      });
  }

  private applyTenantBranding(t: TenantDetailPayload): void {
    const logoStr = (t.logo ?? '').trim();
    this.logoPreviewUrl = logoStr;
    this.existingLogoHint = logoStr
      ? 'Current logo on file — choose a file to replace or remove'
      : '';

    const pp = t.profile_picture;
    const panPreview =
      pp && typeof pp.preview_url === 'string' ? pp.preview_url.trim() : '';
    this.panPreviewUrl = panPreview;
    const hasPanOnFile =
      pp &&
      typeof pp.storage_path === 'string' &&
      pp.storage_path.trim().length > 0;
    this.existingPanImageOnFile = !!hasPanOnFile;
    this.existingProfilePictureHint = hasPanOnFile
      ? 'Current PAN card image on file — choose a file to replace or remove'
      : '';
  }

  private applyCommercialReceiptFields(
    cp: TenantDetailPayload['commercial_profile'],
  ): void {
    if (!cp) {
      return;
    }
    if (cp.amount_received != null && cp.amount_received !== '') {
      this.amountReceived = formatMoneyFieldForForm(cp.amount_received);
    }
    if (cp.un_number) {
      this.unNumber = cp.un_number;
    }
    if (cp.date_of_receipt) {
      const d = new Date(String(cp.date_of_receipt));
      if (!Number.isNaN(d.getTime())) {
        this.dateOfReceipt = d;
        this.originalDateOfReceipt = d;
      }
    }
  }

  private receiptDateToIso(value: unknown): string {
    let d: Date | null = null;
    if (typeof value === 'string') {
      d = new Date(value);
    } else if (value instanceof Date) {
      d = value;
    }
    if (d && !Number.isNaN(d.getTime())) {
      return d.toISOString();
    }
    return '';
  }

  private openSuccessModal(): void {
    this.clearSuccessRedirectTimer();
    this.showSuccessModal = true;
    this.successRedirectTimer = setTimeout(() => {
      this.successRedirectTimer = null;
      this.navigateToList();
    }, 2200);
  }

  closeSuccessModal(): void {
    this.clearSuccessRedirectTimer();
    this.showSuccessModal = false;
    this.navigateToList();
  }

  private navigateToList(): void {
    this.showSuccessModal = false;
    void this.router.navigate(['/manage-client']);
  }

  private clearSuccessRedirectTimer(): void {
    if (this.successRedirectTimer !== null) {
      clearTimeout(this.successRedirectTimer);
      this.successRedirectTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearSuccessRedirectTimer();
  }

  private hasAnyProductSelected(): boolean {
    return hasAnyProductSelected(this.productsCatalog, this.productSelection);
  }
}
